import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import 'moment/locale/ru';
import upperFirst from 'lodash/upperFirst';

import { setDocumentTitleBase } from '../../actions/actions';
import services, { events } from '../../services/services';
import { browserHistory } from '../../browserHistory';
import {
  extractPreviewsFromMessage,
  focusChatTextArea,
  generateRoomURL,
  getRoomName,
  getUserFullName,
} from '../../utils/utilsChat';

import {
  inflect,
  scrollToElement,
  setToLocalStorage,
  isMobileSizeScreen,
  getFromLocalStorage,
  exposeComponentToGlobalScope
} from '../../utils/utilsGlobal';

import {
  updateIssue,
  getIssueById,
  sendIssueMassMessage,
  getIssueLinkedMessages,
  getIssueSentMassMessages,
  getIssueLinkedRooms,
  getIssueHistory,
} from '../../services/apiIssues';

import {
  Dropdown,
  IssueList,
  Modal,
  Avatar,
  Tooltip,
  ChatTextArea,
  LoadingScreen,
  FormattedMarkdown,
  FullScreenPreviews,
  IssueDetailsSidebar,
  ChatMessagesAttachments,
  DEFAULT_ISSUE_LIST_FILTERS,
} from '../../components';

import styles from './IssueDetails.scss';
import uiStyles from '../../styles/ui.scss';


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    isMobileMenuEnabled: state.isMobileMenuEnabled,
    isMobileSidebarEnabled: state.isMobileSidebarEnabled,
  };
};

const mapDispatchToProps = { setDocumentTitleBase };


@connect(mapStateToProps, mapDispatchToProps)
export class IssueDetails extends Component {

  state = {
    issue: {},
    isLoading: false,
    isEditingTitle: false,
    isSubmittingTitle: false,
    sentMassMessages: [],
    linkedRoomsById: {},
    fullScreenPreviews: [],
    history: [],
    isTogglingClosedStatus: false,
    isSubmittingMassMessage: false,
    highlightedMassMessageId: null,
    isMassMessageModalOpen: false,
    massMessageMention: 'ALL',
    massMessageText: '',
    massMessageAttachments: [],
    massMessageMustOpenWidget: false,
    mobileIssueListFilters: {},
    titleText: '',
  };

  rerenderInterval = null;
  massMessageRefs = {};
  fullScreenPreviewer = React.createRef();

  editTitleTextarea = React.createRef();
  scrollableArea = React.createRef();

  MASS_MESSAGE_DROPDOWN = {
    ALL: isMobileSizeScreen() ? 'Всем в компании с проблемой' : 'Упомянуть всех в компании с проблемой',
    SENDERS: isMobileSizeScreen() ? 'Только написавшим о проблеме' : 'Упомянуть только людей, написавших о проблеме',
  };

  componentDidMount(){
    const { issueId } = this.props;
    this.initializeMobileIssueList();

    services.userWatcher.on(events.USER_SET_SUCCESS, currentCompany => {
      this.onIssueIdChange(currentCompany, issueId);
      this.initializeRerender();
    });
    exposeComponentToGlobalScope(this);
  }

  componentDidUpdate(prevProps){
    const { issueId, currentCompany } = this.props;
    if (issueId !== prevProps.issueId) {
      this.onIssueIdChange(currentCompany, issueId);
    }
  }

  componentWillUnmount(){
    clearInterval(this.rerenderInterval);
  };

  initializeRerender = () => {
    // Force rerender every minute to update "N minutes ago" labels in sent mass messages
    this.rerenderInterval = setInterval(() => {
      this.forceUpdate();
    }, 10 * 1000);
  };

  initializeMobileIssueList = () => {
    const locallySavedMobileIssueListFilters = getFromLocalStorage('ISSUES_FILTERS', {});
    this.setState({
      mobileIssueListFilters: {
        ...DEFAULT_ISSUE_LIST_FILTERS,
        ...locallySavedMobileIssueListFilters,
      },
    });
  };

  generateFullScreenPreviews = () => {
    requestAnimationFrame(() => {
      const { sentMassMessages, linkedRoomsById } = this.state;
      const linkedRoomMessages = Object.values(linkedRoomsById)
        .map(room => room.roomLinkedMessages)
        .flat();

      const fullScreenPreviews = sentMassMessages
        .concat(linkedRoomMessages)
        .map(extractPreviewsFromMessage)
        .flat();

      this.setState({ fullScreenPreviews });
    });
  };

  onIssueIdChange = (currentCompany, issueId) => {
    const { setDocumentTitleBase } = this.props;
    const {
      massMessageText = '',
      massMessageMention = 'ALL',
      massMessageMustOpenWidget = false,
    } = getFromLocalStorage('TYPED_MASS_MESSAGE_' + issueId, {});

    this.setState({
      massMessageText,
      massMessageMention,
      massMessageMustOpenWidget,
      isLoading: true,
    });

    return getIssueById(currentCompany.id, issueId)
      .then(issue => {
        this.setState({
          issue,
          titleText: issue.title,
        });
        Promise.all([
          this.fetchLinkedRooms(currentCompany, issueId),
          this.fetchSentMassMessages(currentCompany, issueId),
          this.fetchSidebarHistory(currentCompany, issueId),
        ])
          .then(() => {
            this.generateFullScreenPreviews();
          })
          .finally(() => {
            this.setState({ isLoading: false });
          });
        setDocumentTitleBase(issue.title);
      })
      .catch(e => {
        e.overrideErrorNotification({ alert: false });
        browserHistory.push('/issues');
        throw e;
      });
  };

  fetchSentMassMessages = (currentCompany, issueId) => {
    return getIssueSentMassMessages(currentCompany.id, issueId).then(sentMassMessages => {
      this.setState({ sentMassMessages });
      return sentMassMessages;
    });
  };

  fetchLinkedRooms = (currentCompany, issueId) => {
    return getIssueLinkedRooms(currentCompany.id, issueId).then(linkedRooms => {
      const linkedRoomsById = {};
      linkedRooms.forEach((linkedRoom, index) => {
        linkedRoomsById[linkedRoom.id] = { ...linkedRoom, index };
      });
      this.setState({ linkedRoomsById });
      return linkedRoomsById;
    });
  };

  fetchSidebarHistory = (currentCompany, issueId) => {
    return getIssueHistory(currentCompany.id, issueId).then(history => {
      this.setState({ history });
      return history;
    });
  };

  updateLinkedRoom = (roomId, diff) => {
    const { linkedRoomsById } = this.state;
    const updatedRoom = {
      ...linkedRoomsById[roomId],
      ...diff,
    };

    this.setState({
      linkedRoomsById: {
        ...linkedRoomsById,
        [roomId]: updatedRoom,
      }
    });
  };

  expandLinkedMessageThread = (room) => {
    const { issue } = this.state;
    this.updateLinkedRoom(room.id, { isLoadingLinkedMessages: true });

    getIssueLinkedMessages(issue.id, room.id)
      .then(roomLinkedMessages => {
        this.updateLinkedRoom(room.id, { roomLinkedMessages });
        this.generateFullScreenPreviews();

        requestAnimationFrame(() => {
          this.updateLinkedRoom(room.id, {
            isRoomExpanded: true,
            isLoadingLinkedMessages: false,
          });
        });
      })
      .catch(() => {
        this.updateLinkedRoom(room.id, { isLoadingLinkedMessages: false });
      });
  };

  collapseLinkedMessageThread = (room) => {
    this.updateLinkedRoom(room.id, { isRoomExpanded: false });
    setTimeout(() => {
      this.updateLinkedRoom(room.id, {
        roomLinkedMessages: room.roomLinkedMessages.slice(0, 1),
        isRoomExpanded: false,
      });
    }, 300);
  };

  getLinkedRoomsArray = () => {
    const { linkedRoomsById } = this.state;
    return Object.values(linkedRoomsById).sort((a,b) => a.index - b.index);
  };

  toggleIssueClosedStatus = ({ isClosed }) => {
    const { currentCompany } = this.props;
    const { issue } = this.state;
    const confirmationMessage = isClosed ? 'Отметить проблему решенной?' : 'Переокрыть проблему?';

    if ( confirm(confirmationMessage) ) {
      this.setState({ isTogglingClosedStatus: true });
      updateIssue(currentCompany.id, issue.id, { isClosed })
        .then(issue => {
          this.setState({
            issue,
            isTogglingClosedStatus: false,
          });
          this.fetchSidebarHistory(currentCompany, issue.id);
        })
        .catch(() => {
          this.setState({ isTogglingClosedStatus: false });
        });
    }
  };

  onMassMessageMentionChange = (massMessageMention) => {
    this.setState({ massMessageMention });
    this.saveEnteredMassMessageToLocalStorage({ massMessageMention });
    focusChatTextArea();
  };

  onMassMessageTextareaChange = (diff) => {
    const updatedState = {};
    if ('text' in diff) {
      updatedState.massMessageText = diff.text;
    }
    if ('attachments' in diff) {
      updatedState.massMessageAttachments = diff.attachments;
    }
    if ('mustOpenWidget' in diff) {
      updatedState.massMessageMustOpenWidget = diff.mustOpenWidget;
    }
    this.setState(updatedState);
    this.saveEnteredMassMessageToLocalStorage(updatedState);
  };

  saveEnteredMassMessageToLocalStorage = (updatedMassMessage) => {
    const { issue } = this.state;
    const currentMassMessage = getFromLocalStorage('TYPED_MASS_MESSAGE_' + issue.id, {});
    setToLocalStorage('TYPED_MASS_MESSAGE_' + issue.id, {
      ...currentMassMessage,
      ...updatedMassMessage,
    });
  };

  onMassMessageSubmitClick = () => {
    const { currentCompany } = this.props;
    const {
      issue,
      massMessageText,
      massMessageAttachments,
      massMessageMustOpenWidget,
      massMessageMention,
      sentMassMessages,
    } = this.state;

    if (!massMessageText && !massMessageAttachments.length) {
      return;
    }

    this.setState({ isSubmittingMassMessage: true });

    const params = {
      text: massMessageText,
      attachments: massMessageAttachments,
      mustOpenWidget: massMessageMustOpenWidget,
      mentions: massMessageMention,
    };
    sendIssueMassMessage(currentCompany.id, issue.id, params)
      .then(massMessage => {
        this.setState({
          massMessageText: '',
          massMessageAttachments: [],
          massMessageMustOpenWidget: false,
          massMessageMention: 'ALL',
          isMassMessageModalOpen: false,
          isSubmittingMassMessage: false,
          sentMassMessages: [ massMessage, ...sentMassMessages ],
        });
        this.fetchSidebarHistory(currentCompany, issue.id);
        this.generateFullScreenPreviews();
        this.highlightMassMessage(massMessage.id);
        localStorage.removeItem('TYPED_MASS_MESSAGE_' + issue.id);
      })
      .catch(() => {
        this.setState({ isSubmittingMassMessage: false });
      });
  };

  highlightMassMessage = (highlightedMassMessageId) => {
    const messageElement = this.massMessageRefs[highlightedMassMessageId];

    scrollToElement(messageElement, () => {
      this.setState({ highlightedMassMessageId });
      setTimeout(() => {
        this.setState({ highlightedMassMessageId: null });
      }, 2000);
    });
  };

  onMobileIssueListFilters = (filtersDiff) => {
    const { mobileIssueListFilters } = this.state;
    const updatedMobileIssueListFilters = { ...mobileIssueListFilters, ...filtersDiff };
    setToLocalStorage('ISSUES_FILTERS', updatedMobileIssueListFilters);
    this.setState({ mobileIssueListFilters: updatedMobileIssueListFilters });
  };

  onEditTitleSubmit = () => {
    const { currentCompany } = this.props;
    const { issue, titleText } = this.state;
    this.setState({ isSubmittingTitle: true });

    return updateIssue(currentCompany.id, issue.id, { title: titleText.trim() })
      .then(issue => {
        this.setState({
          issue,
          titleText: issue.title,
          isEditingTitle: false,
        });
      })
      .catch(e => {
        if (e.errorType === 'HAS_ALREADY_BEEN_TAKEN') {
          e.overrideErrorNotification({ errorMessage: 'Проблема с таким названием уже существует' });
        }
      })
      .finally(() => {
        this.setState({ isSubmittingTitle: false });
      });
  };

  onEditTitleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.onEditTitleSubmit();
    }
  };

  onEditTitleClick = () => {
    this.setState({ isEditingTitle: true });
    requestAnimationFrame(() => {
      this.editTitleTextarea.current.focus();
    });
  };

  createMessageRef = (messageElement, message) => {
    this.massMessageRefs[message.id] = messageElement;
  };

  openPreview = (attachmentId) => {
    this.fullScreenPreviewer.current.openPreview(attachmentId);
  };

  render(){
    const {
      currentCompany,
      isMobileMenuEnabled,
      isMobileSidebarEnabled,
      onToggleFullScreenPreview,
    } = this.props;

    const {
      issue,
      history,
      isLoading,
      titleText,
      isEditingTitle,
      isSubmittingTitle,
      isTogglingClosedStatus,
      isMassMessageModalOpen,
      isSubmittingMassMessage,
      highlightedMassMessageId,
      sentMassMessages,
      massMessageMention,
      massMessageText,
      massMessageAttachments,
      massMessageMustOpenWidget,
      mobileIssueListFilters,
      fullScreenPreviews,
    } = this.state;

    if (isLoading || !issue.id) {
      return (
        <LoadingScreen/>
      );
    }

    const linkedRoomsByIdArray = this.getLinkedRoomsArray();
    const isMobileSize = isMobileSizeScreen();
    const noLinkedRooms = !Boolean(linkedRoomsByIdArray.length);

    return (
      <div className={cn({
        [styles.containerMobileMenuEnabled]: isMobileMenuEnabled,
        [styles.containerMobileSidebarEnabled]: isMobileSidebarEnabled,
      })}>

        {isMobileSize && (
          <IssueList className={styles.mobileIssueList}
            onFiltersChange={this.onMobileIssueListFilters}
            filters={mobileIssueListFilters}/>
        )}

        <div className={styles.main} ref={this.scrollableArea}>
          <Link className={styles.backToList} to="/issues">Назад к списку проблем</Link>
          <h2 className={styles.title}>
            <>{issue.title}</>
            <Tooltip title="Переименовать проблему">
              <i className={cn('icon-edit', styles.titleEdit)} onClick={this.onEditTitleClick}/>
            </Tooltip>
          </h2>

          <div className={styles.titleCaption}>
            {inflect(issue.totalLinkedMessagesCount, ['сообщение', 'сообщения', 'сообщений'])}
            &nbsp;в {inflect(issue.totalLinkedRoomsCount, ['чате', 'чатах', 'чатах'])}
            <span className={styles.titleCaptionDot}>&middot;</span>
            <Tooltip title={moment(issue.insertedAt).format('D MMMM YYYY, H:mm:ss')}>
              <span>{upperFirst(moment(issue.insertedAt).fromNow())}</span>
            </Tooltip>
          </div>

          {issue.isClosed && (
            <div className={styles.issueClosedLabel}>
              <i className={cn(styles.issueClosedIcon, 'icon-system-case-closed')}/>
              <b>Проблема решена {moment(issue.closedAt).format('D MMMM YYYY')},</b> {getUserFullName(issue.closedBy)}
            </div>
          )}

          <div className={styles.actions}>
            {!issue.isClosed && (
              <Fragment>
                <button className={cn({
                  [uiStyles.buttonSuccess]: true,
                  [styles.actionsButton]: true,
                  [styles.actionsButtonToggling]: isTogglingClosedStatus,
                })} onClick={() => this.toggleIssueClosedStatus({ isClosed: true })}>
                  Отметить решенной
                </button>
                <button className={cn(styles.actionsButton, uiStyles.buttonSecondary)}
                  disabled={noLinkedRooms}
                  onClick={() => {
                    this.setState({ isMassMessageModalOpen: true }, focusChatTextArea);
                  }}>
                  <span className={styles.actionsButtonHiddenOnSmallScreen}>Отправить&nbsp;</span>
                  массовое сообщение
                </button>
              </Fragment>
            )}
            {issue.isClosed && (
              <button  className={cn({
                [uiStyles.buttonSecondary]: true,
                [styles.actionsButton]: true,
                [styles.actionsButtonToggling]: isTogglingClosedStatus,
              })} onClick={() => this.toggleIssueClosedStatus({ isClosed: false })}>
                Переоткрыть проблему
              </button>
            )}
          </div>


          {Boolean(sentMassMessages.length) && (
            <div className={styles.messagesSentMassMessages}>
              <h3 className={styles.messagesTitle}>Отправленные массовые сообщения</h3>

              {sentMassMessages.map(massMessage => (
                <div className={cn({
                  [styles.messagesItem]: true,
                  [styles.messagesItemHighlighted]: highlightedMassMessageId === massMessage.id,
                })}
                  ref={element => this.createMessageRef(element, massMessage)}
                  key={massMessage.id}>
                  <Avatar className={styles.messagesAvatar} user={massMessage.sender}/>

                  <h4 className={styles.messagesSender}>{getUserFullName(massMessage.sender)}</h4>
                  <FormattedMarkdown className={styles.messagesText} markdown={massMessage.text}/>

                  <ChatMessagesAttachments
                    attachments={massMessage.attachments}
                    onPreviewClick={this.openPreview}/>

                  <div className={styles.messagesDate}>
                    <Tooltip className={styles.messagesDateTooltip}
                      title={moment(massMessage.timestamp).format('D MMMM YYYY, H:mm:ss').replace(',', ',\n')}>
                      <span className={styles.messagesDateText}>
                        {upperFirst(moment(massMessage.timestamp).fromNow())}
                      </span>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}


          <div className={styles.messagesLinkedMessages}>
            {!noLinkedRooms && (
              <h3 className={styles.messagesTitle}>Контекст</h3>
            )}
            {noLinkedRooms && (
              <div className={styles.messagesTitleNotLinkedToMessages}>
                Нет прикрепленных сообщений
              </div>
            )}

            {linkedRoomsByIdArray.map(room => (
              <div key={room.id} className={cn({
                [styles.messagesSection]: true,
                [styles.messagesSectionExpanded]: room.isRoomExpanded,
              })}>

                {room.roomLinkedMessages.map(message => (
                  <div className={styles.messagesItem} key={message.id}>
                    <Avatar className={styles.messagesAvatar} user={message.sender}/>

                    <h4 className={styles.messagesSender}>
                      <Link to={generateRoomURL(currentCompany.id, room.id)} className={uiStyles.linkBlack}>
                        {getRoomName(room)}
                      </Link> → {getUserFullName(message.sender)}
                    </h4>

                    <FormattedMarkdown
                      className={styles.messagesText}
                      markdown={message.text}/>

                    <ChatMessagesAttachments
                      attachments={message.attachments}
                      onPreviewClick={this.openPreview}/>

                    <div className={styles.messagesDate}>
                      <Tooltip className={styles.messagesDateTooltip}
                        title={moment(message.timestamp).format('D MMMM YYYY, H:mm:ss').replace(',', ',\n')}>
                        <span className={styles.messagesDateText}>
                          {upperFirst(moment(message.timestamp).fromNow())}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                ))}

                {room.roomLinkedMessagesCount > 1 && (
                  <div className={styles.messagesActions}>
                    {!room.isRoomExpanded && (
                      <span className={cn({
                        [styles.messagesToggleExpand]: true,
                        [styles.messagesToggleExpandLoading]: room.isLoadingLinkedMessages,
                      })}
                      onClick={() => this.expandLinkedMessageThread(room)}>
                        Еще {inflect(room.roomLinkedMessagesCount - 1, ['сообщение', 'сообщения', 'сообщений'])}
                      </span>
                    )}
                    {room.isRoomExpanded && (
                      <span className={styles.messagesToggleExpand} onClick={() => this.collapseLinkedMessageThread(room)}>
                        Свернуть
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>


        {isMassMessageModalOpen && (
          <Modal title="Массовое сообщение"
            onClose={() => this.setState({ isMassMessageModalOpen: false })}
            verticallyCentered={true}
            overrideClassNames={{
              modal: cn({
                [styles.sendMassMessageModal]: true,
                [styles.sendMassMessageSubmitting]: isSubmittingMassMessage,
              }),
            }}>
            <Dropdown className={styles.sendMassMessageMention}
              buttonAppearance={true}
              defaultValue={massMessageMention}
              onChange={({ value }) => this.onMassMessageMentionChange(value)}
              overrideClassNames={{
                button: styles.sendMassMessageMentionButton,
              }}
              items={[
                { title: this.MASS_MESSAGE_DROPDOWN['ALL'], value: 'ALL' },
                { title: this.MASS_MESSAGE_DROPDOWN['SENDERS'], value: 'SENDERS' },
              ]}>
              {this.MASS_MESSAGE_DROPDOWN[massMessageMention]}
            </Dropdown>

            <ChatTextArea className={styles.sendMassMessageTextarea}
              text={massMessageText}
              attachments={massMessageAttachments}
              placeholder={'Ответ клиентам...'}
              mustOpenWidget={massMessageMustOpenWidget}
              onChange={this.onMassMessageTextareaChange}
              disableMentions={true}
              disableMobileView={true}
              disableBottomButtons={true}
              disableSubmissionOnEnter={true}
              hideActions={{
                REQUEST_SCREENSHOT: true,
                SEND_MESSAGE: true
              }}/>

            <button className={cn(styles.sendMassMessageButton, uiStyles.buttonPrimary)}
              onClick={this.onMassMessageSubmitClick}>
              Отправить
            </button>
          </Modal>
        )}

        <FullScreenPreviews
          previews={fullScreenPreviews}
          ref={this.fullScreenPreviewer}
          onToggle={onToggleFullScreenPreview}
        />

        <IssueDetailsSidebar
          className={styles.sidebar}
          history={history}
          issue={issue}
          onHistoryChange={() => this.fetchSidebarHistory(currentCompany, issue.id)}
          onMassMessageClick={message => this.highlightMassMessage(message.id)}/>

        {isEditingTitle && (
          <Modal
            title="Переименовать проблему"
            onClose={() => this.setState({ isEditingTitle: false })}
            verticallyCentered={true}
            overrideClassNames={{
              modal: styles.editTitleModal
            }}>
            <textarea
              className={styles.editTitleTextarea}
              ref={this.editTitleTextarea}
              value={titleText}
              onKeyDown={this.onEditTitleKeyDown}
              onChange={e => this.setState({ titleText: e.target.value })}/>
            <button className={cn({
              [uiStyles.buttonPrimary]: true,
              [styles.editTitleButtonSubmitting]: isSubmittingTitle,
            })} onClick={this.onEditTitleSubmit}>
              Сохранить
            </button>
          </Modal>
        )}
      </div>
    );
  }
}
